import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera, Building2, Heart, Store, FileText } from "lucide-react";
import api from "../lib/api";
import { setAuth } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function PhotographerRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
    org_number: "",
    address: "",
  });
  const [payoutType, setPayoutType] = useState("sponsorship");
  const [riderName, setRiderName] = useState("");
  const [riderBib, setRiderBib] = useState("");
  const [riderClub, setRiderClub] = useState("");
  const [publishPublic, setPublishPublic] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/agreement").then((r) => setAgreement(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const hasOrg = form.org_number.trim().length > 0;

  // If no org, force sponsorship/platform
  useEffect(() => {
    if (!hasOrg && payoutType === "company") setPayoutType("sponsorship");
  }, [hasOrg, payoutType]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agreementAccepted) {
      setErr("Du måste läsa och godkänna avtalet");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        name: form.full_name,
        full_name: form.full_name,
        company_name: form.company_name || null,
        org_number: form.org_number || null,
        address: form.address || null,
        payout_type: payoutType,
        sponsorship_rider_name: payoutType === "sponsorship" ? riderName : null,
        sponsorship_rider_bib: payoutType === "sponsorship" ? riderBib : null,
        sponsorship_rider_club: payoutType === "sponsorship" ? riderClub : null,
        sponsorship_publish_public: payoutType === "sponsorship" ? publishPublic : false,
        agreement_accepted: true,
      };
      const r = await api.post("/auth/photographer/register", payload);
      setAuth(r.data.token, r.data.photographer, "photographer");
      navigate("/photographer/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registrering misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const payoutOptions = [
    {
      id: "company",
      title: "Företag med F-skatt",
      icon: Building2,
      desc: "Jag har F-skatt/org-nummer. Jag ansluter Stripe Connect och får mina intäkter direkt till mitt företag.",
      disabled: !hasOrg,
      disabledReason: "Kräver org-nummer ovan",
    },
    {
      id: "sponsorship",
      title: "Sponsring",
      icon: Heart,
      desc: "Jag är privatperson. Min intäktsandel används för att köpa utrustning/delar till en förare jag väljer. Enduroskola hanterar allt via Ljunggrens Motor.",
    },
    {
      id: "platform",
      title: "Plattformen (100%)",
      icon: Store,
      desc: "Jag skänker allt till Enduroskola-plattformen för drift och utveckling.",
    },
  ];

  return (
    <div className="App">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Camera className="w-10 h-10 text-brand mb-4" />
        <p className="label-overline mb-2">Bli fotograf</p>
        <h1 className="font-display font-black uppercase text-white text-4xl mb-6">Skapa konto</h1>

        <form onSubmit={submit} className="card-surface p-6 space-y-5" data-testid="photographer-register-form">
          <div>
            <label className="label-overline block mb-1">Fullständigt namn *</label>
            <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="input-field" data-testid="register-full-name" />
          </div>

          <div>
            <label className="label-overline block mb-1">E-post *</label>
            <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input-field" data-testid="register-email" />
          </div>

          <div>
            <label className="label-overline block mb-1">Lösenord (minst 6 tecken) *</label>
            <input required type="password" minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} className="input-field" data-testid="register-password" />
          </div>

          <div className="border-t border-white/10 pt-5">
            <p className="label-overline mb-3">Företagsuppgifter (valfritt om du är privatperson)</p>
            <div className="space-y-3">
              <input placeholder="Företagsnamn" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className="input-field" data-testid="register-company-name" />
              <input placeholder="Org-nummer (t.ex. 556677-8899)" value={form.org_number} onChange={(e) => update("org_number", e.target.value)} className="input-field" data-testid="register-org-number" />
              <input placeholder="Fakturaadress (gata, postnr, ort)" value={form.address} onChange={(e) => update("address", e.target.value)} className="input-field" data-testid="register-address" />
            </div>
          </div>

          <div className="border-t border-white/10 pt-5">
            <label className="label-overline block mb-3">Hur ska dina intäkter hanteras?</label>
            <div className="space-y-2">
              {payoutOptions.map((opt) => {
                const Icon = opt.icon;
                const selected = payoutType === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => !opt.disabled && setPayoutType(opt.id)}
                    className={`w-full text-left border p-4 transition-all flex gap-3 ${
                      opt.disabled
                        ? "border-white/5 opacity-40 cursor-not-allowed"
                        : selected
                        ? "border-brand bg-brand/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`payout-${opt.id}`}
                  >
                    <Icon className={`w-5 h-5 mt-0.5 ${selected && !opt.disabled ? "text-brand" : "text-white/50"}`} />
                    <div>
                      <p className={`font-display font-bold uppercase ${selected && !opt.disabled ? "text-brand" : "text-white"}`}>
                        {opt.title}
                      </p>
                      <p className="text-sm text-white/60 mt-1">{opt.desc}</p>
                      {opt.disabled && <p className="text-xs text-brand mt-1">⚠️ {opt.disabledReason}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {payoutType === "sponsorship" && (
            <div className="border-t border-white/10 pt-5 space-y-3">
              <p className="label-overline">Sponsringsmottagare</p>
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                <input required placeholder="Fullständigt namn på förare *" value={riderName} onChange={(e) => setRiderName(e.target.value)} className="input-field" data-testid="register-rider-name" />
                <input placeholder="Startnr" value={riderBib} onChange={(e) => setRiderBib(e.target.value)} className="input-field" data-testid="register-rider-bib" />
              </div>
              <input placeholder="Klubb (t.ex. SMK Haninge)" value={riderClub} onChange={(e) => setRiderClub(e.target.value)} className="input-field" data-testid="register-rider-club" />
              <label className="flex items-start gap-3 cursor-pointer mt-2" data-testid="register-publish-public-label">
                <input type="checkbox" checked={publishPublic} onChange={(e) => setPublishPublic(e.target.checked)} className="mt-1 accent-brand" data-testid="register-publish-public" />
                <span className="text-sm text-white/70">
                  Publicera föraren och sponsringen publikt på enduroskola-sajten. Andra kan då se att du bidrar till {riderName || "föraren"}.
                </span>
              </label>
            </div>
          )}

          <div className="border-t border-white/10 pt-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreementAccepted} onChange={(e) => setAgreementAccepted(e.target.checked)} className="mt-1 accent-brand" data-testid="register-agreement-accept" />
              <span className="text-sm text-white/80">
                Jag har läst och godkänner{" "}
                <button type="button" onClick={() => setShowAgreement(true)} className="text-brand hover:underline inline-flex items-center gap-1" data-testid="register-view-agreement">
                  <FileText className="w-3 h-3" /> fotografavtalet
                </button>
                {" "}mellan {agreement?.platform?.name || "Plattformen"} och mig.
              </span>
            </label>
          </div>

          {err && <p className="text-destructive text-sm" data-testid="register-error">{err}</p>}
          <button type="submit" disabled={loading || !agreementAccepted} className="btn-primary w-full disabled:opacity-40" data-testid="register-submit">
            {loading ? "Skapar konto..." : "Skapa konto"}
          </button>
        </form>

        <p className="text-white/60 text-sm mt-4 text-center">
          Redan medlem? <Link to="/photographer/login" className="text-brand hover:underline">Logga in</Link>
        </p>
      </div>
      <Footer />

      {showAgreement && agreement && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={() => setShowAgreement(false)}>
          <div className="bg-[#141414] border border-white/20 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display font-bold uppercase text-white text-xl">Fotografavtal v{agreement.version}</h3>
              <button onClick={() => setShowAgreement(false)} className="text-white/60 hover:text-white text-2xl">×</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] text-white/80 text-sm whitespace-pre-wrap">
              {agreement.text}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={() => setShowAgreement(false)} className="btn-secondary text-sm py-2 px-4">Stäng</button>
              <button
                onClick={() => { setAgreementAccepted(true); setShowAgreement(false); }}
                className="btn-primary text-sm py-2 px-4"
                data-testid="register-agreement-accept-modal"
              >
                Jag godkänner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
